import {
  DataProvider,
  GetListResult,
  GetOneParams,
  GetOneResult,
  GetManyParams,
  GetManyResult,
  CreateParams,
  CreateResult,
  UpdateParams,
  UpdateResult,
  Identifier,
} from 'react-admin'
import { ADMINISTRATOR_URL } from '../../api-urls'
import {
  CacheableDataProviderExtensionResult,
  DataProviderExtensionResult,
} from '../../common/data-provider'
import {
  get,
  getWithPagination,
  patch,
  post,
  put,
} from '../../common/fetch.utils'
import {
  filterParamsComposer,
  queryParamsComposer,
} from '../../common/get-by-conditions.utils'
import { TicketAPIGetListParams } from '../../common/ticket-api-get-list.params'
import { RoleDto } from '../../dto/role/role.dto'
import { UserDto } from '../../dto/user.dto'
import filterMapper from './users-filter.mapper'
import { mapSortUserParam } from './users-sort.mapper'
import { NotificationTagDto } from '../../dto/system-notifications/notification-tag.dto'

const provider = {
  getList: async (
    resource: string,
    { filter: { ...filter }, sort, pagination }: TicketAPIGetListParams,
  ): Promise<GetListResult<UserDto>> => {
    const filterParams = `o=>${filterParamsComposer('o', filter, filterMapper)}`
    const pathParams = queryParamsComposer(sort, pagination, mapSortUserParam)
    const path = `/${filterParams}${(pathParams && `?${pathParams}`) || ''}`

    const {
      data,
      range: { total },
    } = await getWithPagination<UserDto[]>(
      `${ADMINISTRATOR_URL}/GetUsersByConditions`,
      path,
    )

    return Promise.resolve({
      data,
      total,
    })
  },
  getOne: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<GetOneResult<UserDto>> => {
    const data = await get<UserDto>(
      `${ADMINISTRATOR_URL}/GetUserById`,
      `/${id}`,
    )
    return Promise.resolve({
      data,
    })
  },
  getMany: async (
    resource: string,
    { ids }: GetManyParams,
  ): Promise<GetManyResult<UserDto>> => {
    const data = await get<UserDto[]>(
      `${ADMINISTRATOR_URL}/GetUsersByConditions`,
      `/u=>new int[] {${ids.toString()}}.Contains(u.Id)`,
    )
    return Promise.resolve({
      data,
    })
  },
  create: async (
    resource: string,
    { data }: CreateParams<CreateUserRequest>,
  ): Promise<CreateResult> => {
    const created = await post<CreateUserRequest, UserDto>(
      `${ADMINISTRATOR_URL}/PostUser`,
      data,
    )
    return Promise.resolve({ data: created })
  },
  update: async (
    resource: string,
    { id, data, previousData }: UpdateParams<UserDto>,
  ): Promise<UpdateResult> => {
    const update = await patch<{ id: Identifier; [n: string]: any }, UserDto>(
      `${ADMINISTRATOR_URL}/PatchUser`,
      {
        id,
        userName:
          data.userName !== previousData.userName ? data.userName : undefined,
        companyId:
          data.companyId !== previousData.companyId
            ? data.companyId
            : undefined,
        requirePasswordUpdate:
          data.requirePasswordUpdate !== previousData.requirePasswordUpdate
            ? data.requirePasswordUpdate
            : undefined,
        email: data.email !== previousData.email ? data.email : undefined,
        phoneNumber:
          data.phoneNumber !== previousData.phoneNumber
            ? data.phoneNumber
            : undefined,
      },
    )
    return Promise.resolve({ data: update })
  },
  changeUserPassword: async (
    resource: string,
    params: ChangeUserPasswordRequest,
  ): Promise<DataProviderExtensionResult<ChangeUserPasswordResponse>> => {
    const newPassword = await put<
      ChangeUserPasswordRequest,
      ChangeUserPasswordResponse
    >(`${ADMINISTRATOR_URL}/ChangeUserPassword`, params)
    return Promise.resolve({
      data: newPassword,
    })
  },
  generateResetUserPasswordToken: async (
    resource: string,
    params: userIdParams,
  ): Promise<
    DataProviderExtensionResult<GenerateResetUserPasswordTokenResponse>
  > => {
    const token = await put<
      userIdParams,
      GenerateResetUserPasswordTokenResponse
    >(`${ADMINISTRATOR_URL}/GenerateResetUserPasswordToken`, params)
    return {
      data: token,
    }
  },
  lockUser: async (
    resource: string,
    params: LockUserParams,
  ): Promise<DataProviderExtensionResult<LockUserResponse>> => {
    const data = await put<LockUserParams, LockUserResponse>(
      `${ADMINISTRATOR_URL}/LockUser`,
      params,
    )
    return {
      data,
    }
  },
  unlockUser: async (
    resource: string,
    params: userIdParams,
  ): Promise<DataProviderExtensionResult<any>> => {
    const data = await put<userIdParams, any>(
      `${ADMINISTRATOR_URL}/UnlockUser`,
      params,
    )
    return {
      data,
    }
  },
  attachRoles: async (
    resource: string,
    params: UserRoles,
  ): Promise<DataProviderExtensionResult<UserRoles>> => {
    const token = await put<UserRoles, UserRoles>(
      `${ADMINISTRATOR_URL}/AttachRolesToUser`,
      params,
    )
    return {
      data: token,
    }
  },
  detachRoles: async (
    resource: string,
    params: UserRoles,
  ): Promise<DataProviderExtensionResult<UserRoles>> => {
    const token = await put<UserRoles, UserRoles>(
      `${ADMINISTRATOR_URL}/DetachRolesFromUser`,
      params,
    )
    return {
      data: token,
    }
  },
  getRoles: async (
    resource: string,
    { userId }: userIdParams,
  ): Promise<DataProviderExtensionResult<RoleDto[]>> => {
    const token = await get<RoleDto[]>(
      `${ADMINISTRATOR_URL}/GetUserRoles/`,
      userId as string,
    )
    return {
      data: token,
    }
  },
  getUserNotificationTags: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<DataProviderExtensionResult<NotificationTagDto[]>> => {
    const data = await get<NotificationTagDto[]>(
      `${ADMINISTRATOR_URL}/GetUserNotificationTags/`,
      id as string,
    )
    return {
      data,
    }
  },
  setUserNotificationTags: async (
    resource: string,
    params: UserNotificationTags,
  ): Promise<DataProviderExtensionResult<UserNotificationTags>> => {
    const data = await put<UserNotificationTags, UserNotificationTags>(
      `${ADMINISTRATOR_URL}/SetUserNotificationTags/`,
      params,
    )
    return {
      data,
    }
  },
  attachNotificationTagsToUser: async (
    resource: string,
    params: UserNotificationTags,
  ): Promise<DataProviderExtensionResult<UserNotificationTags>> => {
    const data = await put<UserNotificationTags, UserNotificationTags>(
      `${ADMINISTRATOR_URL}/AttachNotificationTagsToUser/`,
      params,
    )
    return {
      data,
    }
  },
  detachNotificationTagsFromUser: async (
    resource: string,
    params: UserNotificationTags,
  ): Promise<DataProviderExtensionResult<UserNotificationTags>> => {
    const data = await put<UserNotificationTags, UserNotificationTags>(
      `${ADMINISTRATOR_URL}/DetachNotificationTagsFromUser/`,
      params,
    )
    return {
      data,
    }
  },
} as UserDataProvider

interface UserDataProvider extends DataProvider {
  changeUserPassword: (
    resource: string,
    params: ChangeUserPasswordRequest,
  ) => Promise<CacheableDataProviderExtensionResult<ChangeUserPasswordResponse>>

  generateResetUserPasswordToken: (
    resource: string,
    params: userIdParams,
  ) => Promise<
    CacheableDataProviderExtensionResult<GenerateResetUserPasswordTokenResponse>
  >

  lockUser: (
    resource: string,
    params: LockUserParams,
  ) => Promise<CacheableDataProviderExtensionResult<LockUserResponse>>

  unlockUser: (resource: string, params: userIdParams) => Promise<any>

  attachRoles: (
    resource: string,
    params: UserRoles,
  ) => Promise<CacheableDataProviderExtensionResult<UserRoles>>

  detachRoles: (
    resource: string,
    params: UserRoles,
  ) => Promise<CacheableDataProviderExtensionResult<UserRoles>>

  getRoles: (
    resource: string,
    params: userIdParams,
  ) => Promise<CacheableDataProviderExtensionResult<RoleDto[]>>

  getUserNotificationTags: (
    resource: string,
    params: GetOneParams,
  ) => Promise<CacheableDataProviderExtensionResult<NotificationTagDto[]>>

  setUserNotificationTags: (
    resource: string,
    params: UserNotificationTags,
  ) => Promise<CacheableDataProviderExtensionResult<UserNotificationTags>>

  attachNotificationTagsToUser: (
    resource: string,
    params: UserNotificationTags,
  ) => Promise<CacheableDataProviderExtensionResult<UserNotificationTags>>

  detachNotificationTagsFromUser: (
    resource: string,
    params: UserNotificationTags,
  ) => Promise<CacheableDataProviderExtensionResult<UserNotificationTags>>
}

export interface CreateUserRequest {
  readonly userName: string
  readonly password: string
  readonly companyId: number
  readonly requirePasswordUpdate: boolean
  readonly email: string
  readonly phoneNumber: string
}

interface ChangeUserPasswordRequest {
  readonly userId: number
  readonly newPassword: string
}

interface ChangeUserPasswordResponse {
  readonly password: string
  readonly id: number
  readonly userName: string
}

interface userIdParams {
  readonly userId: Identifier
}

interface GenerateResetUserPasswordTokenResponse {
  readonly userId: number
  readonly token: string
}

interface LockUserParams {
  readonly userId: number
  readonly lockOutLength: string
}

interface LockUserResponse {
  readonly userId: number
  readonly lockedOutUntil: string
}

interface UserRoles {
  readonly userId: number
  readonly rolesIds: number[]
}

interface GetRolesParams {
  readonly userId: Identifier
}

interface UserNotificationTags {
  readonly userId: number
  readonly notificationTagsIds: number[]
}

export default provider
